import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import styled from 'styled-components'
import { linkedinRecommendations, letterRecommendations } from '../../data/constants'

/* ─────────────────────────────────────────────
   Layout
───────────────────────────────────────────── */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 30px;
  position: relative;
  z-index: 1;
  background: linear-gradient(180deg, rgba(133,76,230,0.03) 0%, rgba(133,76,230,0.07) 100%);
  scroll-margin-top: 100px;
  @media (max-width: 960px) { padding: 40px 16px; }
`

const Wrapper = styled.div`
  width: 100%;
  max-width: 1100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`

/* ─────────────────────────────────────────────
   Header
───────────────────────────────────────────── */
const Title = styled.h2`
  font-family: 'Mosca Laroke', 'Playfair Display', 'Bodoni Moda', 'Cinzel', serif;
  font-size: 42px;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  line-height: 1.2;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 24px;
  letter-spacing: 0.5px;

  @media (max-width: 1200px) { font-size: 36px; }
  @media (max-width: 768px)  { font-size: 28px; }
`;

/* ─────────────────────────────────────────────
   Unified grid
───────────────────────────────────────────── */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 26px;
  width: 100%;
`

/* ─────────────────────────────────────────────
   Shared card base
───────────────────────────────────────────── */
const Card = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.primary + '33'};
  border-radius: 20px;
  padding: 26px 24px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;

  /* large decorative quote on ALL cards */
  &::after {
    content: '❝';
    position: absolute;
    top: 6px; right: 16px;
    font-size: 72px;
    line-height: 1;
    color: ${({ theme }) => theme.primary + '14'};
    font-family: Georgia, serif;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-6px);
    border-color: ${({ theme }) => theme.primary + '77'};
    box-shadow: 0 14px 40px rgba(133,76,230,0.22);
  }
`

/* ─────────────────────────────────────────────
   Source badge  (LinkedIn / LOR)
───────────────────────────────────────────── */
const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  background: ${({ bg }) => bg};
  color: #fff;
`

const LIIconSmall = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: #fff;
  border-radius: 3px;
  color: #0077B5;
  font-size: 9px;
  font-weight: 900;
  font-family: Arial, sans-serif;
  line-height: 1;
  flex-shrink: 0;
`

/* ─────────────────────────────────────────────
   Person header
───────────────────────────────────────────── */
const PersonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`

const InitialsAvatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 2px solid ${({ borderColor }) => borderColor || '#854CE6'};
  background: ${({ borderColor }) => `radial-gradient(circle at 35% 35%, ${borderColor || '#854CE6'}ee, ${borderColor || '#854CE6'}77)`};
  box-shadow: 0 0 12px ${({ borderColor }) => borderColor || '#854CE6'}55, inset 0 0 6px rgba(255,255,255,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  color: #fff;
  flex-shrink: 0;
  letter-spacing: 1px;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 1px 6px rgba(0,0,0,0.5);
  user-select: none;
`

const NameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
`

const Name = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TitleRow = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-wrap: nowrap;
`

const SubTitle = styled.span`
  color: ${({ theme }) => theme.text_secondary};
`

const Dot = styled.span`
  color: ${({ theme }) => theme.text_secondary};
  opacity: 0.5;
`

const OrgName = styled.span`
  font-weight: 600;
  color: ${({ color }) => color || '#854CE6'};
`

/* ─────────────────────────────────────────────
   Extra meta (relation tag / subject line)
───────────────────────────────────────────── */
const RelationTag = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.text_secondary};
  font-style: italic;
  padding: 4px 10px;
  background: ${({ theme }) => theme.primary + '15'};
  border-radius: 20px;
  display: inline-block;
  width: fit-content;
`

/* ─────────────────────────────────────────────
   Body text & Read More Button
───────────────────────────────────────────── */
const BodyText = styled.p`
  font-size: 12.5px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.75;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex-grow: 1;
`

const ReadMoreBtn = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary};
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  transition: opacity 0.2s ease;

  &:hover {
    text-decoration: underline;
  }
`

/* ─────────────────────────────────────────────
   Full-letter modal
───────────────────────────────────────────── */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.78);
  backdrop-filter: blur(10px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.2s ease;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`

const ModalBox = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.primary + '44'};
  border-radius: 24px;
  padding: 40px 44px;
  max-width: 680px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 24px 80px rgba(133,76,230,0.35);
  animation: slideUp 0.25s ease;
  @keyframes slideUp {
    from { transform: translateY(28px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }
  @media (max-width: 600px) { padding: 28px 22px; }
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #854CE6; border-radius: 4px; }
`

const ModalClose = styled.button`
  position: absolute;
  top: 18px; right: 22px;
  background: ${({ theme }) => theme.primary + '22'};
  border: none;
  color: ${({ theme }) => theme.text_primary};
  font-size: 18px;
  width: 34px; height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: ${({ theme }) => theme.primary + '55'}; }
`

const ModalHeader = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 22px;
  padding-right: 40px;
`

const ModalInitialsAvatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 2px solid ${({ color }) => color || '#854CE6'};
  background: ${({ color }) => `radial-gradient(circle at 35% 35%, ${color || '#854CE6'}ee, ${color || '#854CE6'}77)`};
  box-shadow: 0 0 22px ${({ color }) => color || '#854CE6'}66, inset 0 0 10px rgba(255,255,255,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  flex-shrink: 0;
  letter-spacing: 1.5px;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 1px 8px rgba(0,0,0,0.5);
  user-select: none;
`

const ModalMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const ModalName = styled.span`
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
`

const ModalSub = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.text_secondary};
`

const ModalOrg = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ color }) => color || '#854CE6'};
`

const ModalDate = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 2px;
`

const ModalSubject = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  padding: 10px 16px;
  background: ${({ theme }) => theme.primary + '14'};
  border-radius: 10px;
  border-left: 4px solid ${({ color }) => color || '#854CE6'};
  margin-bottom: 22px;
`

const ModalDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.primary + '22'};
  margin-bottom: 22px;
`

const LetterBody = styled.pre`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.85;
  white-space: pre-wrap;
  word-break: break-word;
`

/* ─────────────────────────────────────────────
   Helper
───────────────────────────────────────────── */
const getInitials = (name = '') =>
  name
    .replace(/^(Dr|Prof|Mr|Mrs|Ms)\.?\s*/i, '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('')

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */
const Testimonials = () => {
  const [selectedLetter, setSelectedLetter] = useState(null)

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSelectedLetter(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const openItemModal = (item, isLetter = false) => {
    setSelectedLetter({
      from: isLetter ? item.from : item.name,
      fromTitle: isLetter ? item.fromTitle : item.title,
      fromOrg: isLetter ? item.fromOrg : item.company,
      date: isLetter ? item.date : item.date,
      subject: isLetter ? item.subject : `Recommendation by ${item.name}`,
      fullText: isLetter ? item.fullText : item.text,
      color: item.color || (isLetter ? '#854CE6' : '#0077B5')
    });
  };

  return (
    <Container id="testimonials">
      <Wrapper
        as={motion.div}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <Title>TESTIMONIALS & RECOMMENDATIONS</Title>

        <Grid>
          {/* ── Box 1: Deepak (linkedin[0]) ── */}
          {[linkedinRecommendations[0]].map((rec) => (
            <Card key={`li-${rec.id}`} onClick={() => openItemModal(rec)}>
              <BadgeRow>
                <Badge bg="#0077B5">📄 Letter of Recommendation</Badge>
              </BadgeRow>
              <PersonRow>
                <InitialsAvatar borderColor={rec.color || '#0077B5'}>{getInitials(rec.name)}</InitialsAvatar>
                <NameBlock>
                  <Name>{rec.name}</Name>
                  <TitleRow>
                    <SubTitle>{rec.title}</SubTitle>
                    <Dot>·</Dot>
                    <OrgName color="#0077B5">{rec.company}</OrgName>
                  </TitleRow>
                </NameBlock>
              </PersonRow>
              <RelationTag>{rec.relation}</RelationTag>
              <BodyText>{rec.text}</BodyText>
              <ReadMoreBtn>Read Full Recommendation →</ReadMoreBtn>
            </Card>
          ))}

          {/* ── Box 2: Kevin (linkedin[2]) ── */}
          {[linkedinRecommendations[2]].map((rec) => (
            <Card key={`li-${rec.id}`} onClick={() => openItemModal(rec)}>
              <BadgeRow>
                <Badge bg="#0077B5">📄 Letter of Recommendation</Badge>
              </BadgeRow>
              <PersonRow>
                <InitialsAvatar borderColor={rec.color || '#0077B5'}>{getInitials(rec.name)}</InitialsAvatar>
                <NameBlock>
                  <Name>{rec.name}</Name>
                  <TitleRow>
                    <SubTitle>{rec.title}</SubTitle>
                    <Dot>·</Dot>
                    <OrgName color="#0077B5">{rec.company}</OrgName>
                  </TitleRow>
                </NameBlock>
              </PersonRow>
              <RelationTag>{rec.relation}</RelationTag>
              <BodyText>{rec.text}</BodyText>
              <ReadMoreBtn>Read Full Recommendation →</ReadMoreBtn>
            </Card>
          ))}

          {/* ── Box 3: Anand Kamath (letter[0]) ── */}
          {[letterRecommendations[0]].map((letter) => (
            <Card key={`lor-${letter.id}`} onClick={() => openItemModal(letter, true)}>
              <BadgeRow>
                <Badge bg="#0077B5">📄 Letter of Recommendation</Badge>
              </BadgeRow>
              <PersonRow>
                <InitialsAvatar borderColor="#854CE6">{getInitials(letter.from)}</InitialsAvatar>
                <NameBlock>
                  <Name>{letter.from}</Name>
                  <TitleRow>
                    <SubTitle>{letter.fromTitle}</SubTitle>
                    <Dot>·</Dot>
                    <OrgName color="#0077B5">{letter.fromOrg}</OrgName>
                  </TitleRow>
                </NameBlock>
              </PersonRow>
              <RelationTag>{letter.date}</RelationTag>
              <BodyText>{letter.preview}</BodyText>
              <ReadMoreBtn>Read Full Letter →</ReadMoreBtn>
            </Card>
          ))}

          {/* ── Box 4: Smit (linkedin[1]) ── */}
          {[linkedinRecommendations[1]].map((rec) => (
            <Card key={`li-${rec.id}`} onClick={() => openItemModal(rec)}>
              <BadgeRow>
                <Badge bg="#0077B5">
                  <LIIconSmall>in</LIIconSmall>
                  LinkedIn Recommendation
                </Badge>
              </BadgeRow>
              <PersonRow>
                <InitialsAvatar borderColor={rec.color || '#0077B5'}>{getInitials(rec.name)}</InitialsAvatar>
                <NameBlock>
                  <Name>{rec.name}</Name>
                  <TitleRow>
                    <SubTitle>{rec.title}</SubTitle>
                    <Dot>·</Dot>
                    <OrgName color="#0077B5">{rec.company}</OrgName>
                  </TitleRow>
                </NameBlock>
              </PersonRow>
              <RelationTag>{rec.relation}</RelationTag>
              <BodyText>{rec.text}</BodyText>
              <ReadMoreBtn>Read Full Recommendation →</ReadMoreBtn>
            </Card>
          ))}

          {/* ── Box 5: Martin Reisslein (linkedin[3]) ── */}
          {[linkedinRecommendations[3]].map((rec) => (
            <Card key={`li-${rec.id}`} onClick={() => openItemModal(rec)}>
              <BadgeRow>
                <Badge bg="#0077B5">📄 Letter of Recommendation</Badge>
              </BadgeRow>
              <PersonRow>
                <InitialsAvatar borderColor={rec.color || '#0077B5'}>{getInitials(rec.name)}</InitialsAvatar>
                <NameBlock>
                  <Name>{rec.name}</Name>
                  <TitleRow>
                    <SubTitle>{rec.title}</SubTitle>
                    <Dot>·</Dot>
                    <OrgName color="#0077B5">{rec.company}</OrgName>
                  </TitleRow>
                </NameBlock>
              </PersonRow>
              <RelationTag>{rec.relation}</RelationTag>
              <BodyText>{rec.text}</BodyText>
              <ReadMoreBtn>Read Full Recommendation →</ReadMoreBtn>
            </Card>
          ))}

          {/* ── Box 6: Rajesh P. Sheth (letter[1]) ── */}
          {[letterRecommendations[1]].map((letter) => (
            <Card key={`lor-${letter.id}`} onClick={() => openItemModal(letter, true)}>
              <BadgeRow>
                <Badge bg="#0077B5">📄 Letter of Recommendation</Badge>
              </BadgeRow>
              <PersonRow>
                <InitialsAvatar borderColor="#0077B5">{getInitials(letter.from)}</InitialsAvatar>
                <NameBlock>
                  <Name>{letter.from}</Name>
                  <TitleRow>
                    <SubTitle>{letter.fromTitle}</SubTitle>
                    <Dot>·</Dot>
                    <OrgName color="#0077B5">{letter.fromOrg}</OrgName>
                  </TitleRow>
                </NameBlock>
              </PersonRow>
              <RelationTag>{letter.date}</RelationTag>
              <BodyText>{letter.preview}</BodyText>
              <ReadMoreBtn>Read Full Letter →</ReadMoreBtn>
            </Card>
          ))}
        </Grid>
      </Wrapper>

      {/* ── Full-letter modal ── */}
      {selectedLetter && (
        <ModalOverlay onClick={() => setSelectedLetter(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalClose onClick={() => setSelectedLetter(null)}>✕</ModalClose>

            <ModalHeader>
              <ModalInitialsAvatar color={selectedLetter.color}>{getInitials(selectedLetter.from)}</ModalInitialsAvatar>
              <ModalMeta>
                <ModalName>{selectedLetter.from}</ModalName>
                <ModalSub>{selectedLetter.fromTitle}</ModalSub>
                <ModalOrg color={selectedLetter.color}>{selectedLetter.fromOrg}</ModalOrg>
                <ModalDate>{selectedLetter.date}</ModalDate>
              </ModalMeta>
            </ModalHeader>

            <ModalSubject color={selectedLetter.color}>📋 {selectedLetter.subject}</ModalSubject>
            <ModalDivider />
            <LetterBody>{selectedLetter.fullText}</LetterBody>
          </ModalBox>
        </ModalOverlay>
      )}
    </Container>
  )
}

export default Testimonials
